import React, { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { auth } from "../../firebase.js";
import { fetchPdfExportJob, downloadPdfExportJob } from "../../utils/exportUtils";

import "../../css/DocumentationMainPage.css";
import "../../css/Document.css";
import "../../css/PdfExportDownload.css";

/**
 * Where the "Your PDF export is ready" notification lands (BBD-119):
 * /exports/pdf/:jobId.
 *
 * Large exports are rendered by the backend's Celery worker and kept for 24
 * hours. The file can only be fetched with the requester's token, so the
 * notification cannot link to it directly — this page fetches it and hands it
 * to the browser. It also covers a job that is still running (it polls) and
 * one that failed or expired.
 */

const POLL_INTERVAL_MS = 4000;

function PdfExportDownload() {
    const { jobId } = useParams();
    const [user, setUser] = useState(undefined); // undefined = auth not resolved yet
    const [job, setJob] = useState(null);
    const [missing, setMissing] = useState(false);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const autoStarted = useRef(false);

    useEffect(() => auth.onAuthStateChanged((current) => setUser(current || null)), []);

    useEffect(() => {
        if (!user) return undefined;
        let cancelled = false;
        let timer = null;

        const load = async () => {
            try {
                const token = await user.getIdToken();
                const data = await fetchPdfExportJob(token, jobId);
                if (cancelled) return;
                if (!data) {
                    setMissing(true);
                    return;
                }
                setJob(data);
                if (data.status === "pending" || data.status === "running") {
                    timer = setTimeout(load, POLL_INTERVAL_MS);
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            }
        };

        load();
        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [user, jobId]);

    const download = async () => {
        if (downloading || !user || !job) return;
        setDownloading(true);
        setError(null);
        try {
            await downloadPdfExportJob(await user.getIdToken(), job);
        } catch (err) {
            setError(err.message);
        } finally {
            setDownloading(false);
        }
    };

    // Start the download as soon as the file is ready, once.
    useEffect(() => {
        if (job && job.status === "completed" && !autoStarted.current) {
            autoStarted.current = true;
            download();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [job]);

    const expires = job && job.expires_at
        ? new Date(job.expires_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
        : null;

    let body;
    if (user === undefined) {
        body = <p className="PdfExportDownload__text">Checking your sign-in…</p>;
    } else if (user === null) {
        body = (
            <>
                <p className="PdfExportDownload__text">Log in to download this export. Only the person who requested it can.</p>
                <Link to="/login" className="PdfExportDownload__button">Log in</Link>
            </>
        );
    } else if (missing) {
        body = (
            <p className="PdfExportDownload__text">
                This export could not be found. Exports are kept for 24 hours, and only the person
                who requested one can download it. Export the document again to get a fresh copy.
            </p>
        );
    } else if (!job) {
        body = <p className="PdfExportDownload__text">{error || "Loading your export…"}</p>;
    } else if (job.status === "failed") {
        body = <p className="PdfExportDownload__text">{job.error || "This export could not be generated."} Please export the document again.</p>;
    } else if (job.status !== "completed") {
        body = (
            <p className="PdfExportDownload__text" role="status">
                Your PDF is still being prepared ({job.document_count} document{job.document_count === 1 ? "" : "s"}).
                This page will update when it is ready.
            </p>
        );
    } else {
        body = (
            <>
                <p className="PdfExportDownload__text">
                    <strong>{job.title}</strong> is ready
                    {job.document_count > 1 ? ` (${job.document_count} documents in one PDF)` : ""}.
                    {expires && <> The file is kept until {expires}.</>}
                </p>
                <button type="button" className="PdfExportDownload__button" onClick={download} disabled={downloading}>
                    <i className="fa-solid fa-file-pdf"></i>
                    {downloading ? "Downloading…" : "Download PDF"}
                </button>
                {error && <p className="PdfExportDownload__error" role="alert">{error}</p>}
            </>
        );
    }

    return (
        <div className="DocumentationMainPage__div">
            <div className="PdfExportDownload">
                <div className="PdfExportDownload__card">
                    <h1 className="PdfExportDownload__title">PDF export</h1>
                    {body}
                    <Link to="/documentation" className="PdfExportDownload__back">Back to your documents</Link>
                </div>
            </div>
        </div>
    );
}

export default PdfExportDownload;
